import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { MarketingService } from '@/services/marketing.service';
import { subDays } from 'date-fns';

export const MarketingAnalytics: React.FC = () => {
  const { profile } = useAuth();
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

  const { data: campaigns = [] } = useQuery({
    queryKey: ['marketingCampaigns'],
    queryFn: () => MarketingService.getAllCampaigns(),
    enabled: !!profile,
  });

  const { data: emailCampaigns = [] } = useQuery({
    queryKey: ['emailCampaigns'],
    queryFn: () => MarketingService.getAllEmailCampaigns(),
    enabled: !!profile,
  });

  const { data: contentPosts = [] } = useQuery({
    queryKey: ['contentPosts'],
    queryFn: () => MarketingService.getAllContentPosts(),
    enabled: !!profile,
  });

  // Filter data by time range
  const getDateRange = () => {
    const now = new Date();
    switch (timeRange) {
      case '7d':
        return { start: subDays(now, 7), end: now };
      case '30d':
        return { start: subDays(now, 30), end: now };
      case '90d':
        return { start: subDays(now, 90), end: now };
      default:
        return { start: new Date(0), end: now };
    }
  };

  const dateRange = getDateRange();
  const filteredCampaigns = campaigns.filter(c => {
    if (!c.startDate) return false;
    return c.startDate >= dateRange.start && c.startDate <= dateRange.end;
  });

  const filteredEmailCampaigns = emailCampaigns.filter(e => {
    if (!e.sentAt && !e.scheduledAt) return false;
    const date = e.sentAt || e.scheduledAt;
    return date && date >= dateRange.start && date <= dateRange.end;
  });

  // Calculate metrics
  const totalSpent = filteredCampaigns.reduce((sum, c) => sum + (c.spent || 0), 0);
  const totalBudget = filteredCampaigns.reduce((sum, c) => sum + (c.budget || 0), 0);
  const totalLeads = filteredCampaigns.reduce((sum, c) => sum + (c.metrics?.leads || 0), 0);
  const totalRevenue = filteredCampaigns.reduce((sum, c) => sum + (c.metrics?.revenue || 0), 0);
  const totalImpressions = filteredCampaigns.reduce((sum, c) => sum + (c.metrics?.impressions || 0), 0);
  const totalCampaignClicks = filteredCampaigns.reduce((sum, c) => sum + (c.metrics?.clicks || 0), 0);
  const totalConversions = filteredCampaigns.reduce((sum, c) => sum + (c.metrics?.conversions || 0), 0);

  // Email metrics
  const totalEmailsSent = filteredEmailCampaigns.reduce((sum, e) => sum + e.recipientCount, 0);
  const totalEmailOpens = filteredEmailCampaigns.reduce((sum, e) => sum + (e.openedCount || 0), 0);
  const totalEmailClicks = filteredEmailCampaigns.reduce((sum, e) => sum + (e.clickedCount || 0), 0);
  const avgOpenRate = totalEmailsSent > 0 ? (totalEmailOpens / totalEmailsSent) * 100 : 0;
  const avgClickRate = totalEmailsSent > 0 ? (totalEmailClicks / totalEmailsSent) * 100 : 0;

  // Content metrics
  const publishedContent = contentPosts.filter(p => p.status === 'published');
  const totalViews = publishedContent.reduce((sum, p) => sum + (p.metrics?.views || 0), 0);
  const totalLikes = publishedContent.reduce((sum, p) => sum + (p.metrics?.likes || 0), 0);
  const totalShares = publishedContent.reduce((sum, p) => sum + (p.metrics?.shares || 0), 0);

  // Calculate ROI
  const roi = totalSpent > 0 ? ((totalRevenue - totalSpent) / totalSpent) * 100 : 0;
  const costPerLead = totalLeads > 0 ? totalSpent / totalLeads : 0;
  const costPerAcquisition = totalConversions > 0 ? totalSpent / totalConversions : 0;
  const conversionRate = totalCampaignClicks > 0 ? (totalConversions / totalCampaignClicks) * 100 : 0;
  const clickThroughRate = totalImpressions > 0 ? (totalCampaignClicks / totalImpressions) * 100 : 0;

  // Top performing campaigns
  const topCampaigns = [...filteredCampaigns]
    .sort((a, b) => {
      const aROI = (a.metrics?.roi || 0);
      const bROI = (b.metrics?.roi || 0);
      return bROI - aROI;
    })
    .slice(0, 5);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Marketing Analytics</h2>
        <div className="flex space-x-2">
          {(['7d', '30d', '90d', 'all'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                timeRange === range
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {range === 'all' ? 'All Time' : range.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Campaign Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Spent</div>
          <div className="text-2xl font-bold text-red-600">${totalSpent.toLocaleString()}</div>
          {totalBudget > 0 && (
            <div className="text-xs text-gray-500 mt-1">
              of ${totalBudget.toLocaleString()} budget
            </div>
          )}
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Leads</div>
          <div className="text-2xl font-bold text-blue-600">{totalLeads.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">
            ${costPerLead.toFixed(2)} per lead
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Revenue</div>
          <div className="text-2xl font-bold text-green-600">${totalRevenue.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">
            ROI: {roi.toFixed(1)}%
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Conversions</div>
          <div className="text-2xl font-bold text-purple-600">{totalConversions.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">
            {conversionRate.toFixed(2)}% rate
          </div>
        </div>
      </div>

      {/* Engagement Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Impressions</div>
          <div className="text-2xl font-bold text-gray-900">{totalImpressions.toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Clicks</div>
          <div className="text-2xl font-bold text-indigo-600">{totalCampaignClicks.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">
            {clickThroughRate.toFixed(2)}% CTR
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Email Opens</div>
          <div className="text-2xl font-bold text-teal-600">{totalEmailOpens.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">
            {avgOpenRate.toFixed(2)}% open rate
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Email Clicks</div>
          <div className="text-2xl font-bold text-cyan-600">{totalEmailClicks.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">
            {avgClickRate.toFixed(2)}% click rate
          </div>
        </div>
      </div>

      {/* Content Engagement */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Content Views</div>
          <div className="text-2xl font-bold text-pink-600">{totalViews.toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Content Likes</div>
          <div className="text-2xl font-bold text-red-600">{totalLikes.toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Content Shares</div>
          <div className="text-2xl font-bold text-blue-600">{totalShares.toLocaleString()}</div>
        </div>
      </div>

      {/* Top Performing Campaigns */}
      {topCampaigns.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Campaigns</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Spent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ROI
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Leads
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conversions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {topCampaigns.map((campaign) => {
                  const campaignROI = campaign.metrics?.roi || 0;
                  return (
                    <tr key={campaign.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {campaign.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        ${(campaign.spent || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        ${(campaign.metrics?.revenue || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={campaignROI >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {campaignROI.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {campaign.metrics?.leads || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {campaign.metrics?.conversions || 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Performance Summary */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Campaign Performance</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Average Cost per Lead: ${costPerLead.toFixed(2)}</li>
              <li>• Average Cost per Acquisition: ${costPerAcquisition.toFixed(2)}</li>
              <li>• Conversion Rate: {conversionRate.toFixed(2)}%</li>
              <li>• Click-Through Rate: {clickThroughRate.toFixed(2)}%</li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Email Performance</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Total Emails Sent: {totalEmailsSent.toLocaleString()}</li>
              <li>• Average Open Rate: {avgOpenRate.toFixed(2)}%</li>
              <li>• Average Click Rate: {avgClickRate.toFixed(2)}%</li>
              <li>• Total Opens: {totalEmailOpens.toLocaleString()}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

